import { useState } from 'react'
import { useTheme } from '../hooks/useTheme'

type CapStatus = 'Validated' | 'Pending' | 'Gap'

interface Capability {
  id: string
  name: string
  description: string
  status: CapStatus
  notes: string
}

const MUST_HAVE: Capability[] = [
  {
    id: 'CAP-01', name: 'Real-time ATM Status Monitoring',
    description: 'Sub-second status updates para todos los 933 ATMs de la flota Banreservas',
    status: 'Validated',
    notes: 'WebSocket XFS + SNMP Hybrid demostrado en simulación con latencia p95 <240ms',
  },
  {
    id: 'CAP-02', name: 'Predictive Cash Management',
    description: 'Algoritmo de predicción de agotamiento de efectivo con alertas anticipadas',
    status: 'Validated',
    notes: 'Modelos de tendencia por cassette implementados; precisión >87% en simulaciones 24h',
  },
  {
    id: 'CAP-03', name: 'Multi-Model Error Normalization',
    description: 'Catálogo canónico que unifica códigos nativos de NCR, GRG y fabricantes mixtos',
    status: 'Validated',
    notes: 'Catálogo con 24+ códigos canónicos cubriendo CDM/IDC/PTR/NET/SEC/BNA/SYS/UPS',
  },
  {
    id: 'CAP-04', name: 'Geographic Fleet Visualization',
    description: 'Mapa interactivo con estado en tiempo real, filtros por zona, modelo y status',
    status: 'Validated',
    notes: 'React-Leaflet con CircleMarkers geoposicionados para las 4 zonas dominicanas',
  },
  {
    id: 'CAP-05', name: 'Automated Incident Escalation',
    description: 'Motor de reglas P1-P4 con escalamiento automático a brigadas asignadas',
    status: 'Pending',
    notes: 'Lógica de priorización implementada; integración SMTP/SMS pendiente de credenciales',
  },
  {
    id: 'CAP-06', name: 'Regulatory Compliance Reporting',
    description: 'Reportes automáticos de disponibilidad para cumplimiento JMBR/SIB',
    status: 'Gap',
    notes: 'Motor de reportes diseñado; plantillas JMBR pendientes de especificación oficial',
  },
]

interface UATCriteria {
  id: string
  scenario: string
  acceptance: string
  priority: 'Critical' | 'High' | 'Medium'
  status: 'Pass' | 'Pending' | 'Fail'
}

const UAT_CRITERIA: UATCriteria[] = [
  { id: 'UAT-01', scenario: 'Fallo CDM en ATM de Alta Demanda', acceptance: 'Alerta P1 generada en <30s; brigada notificada en <2min', priority: 'Critical', status: 'Pass' },
  { id: 'UAT-02', scenario: 'Agotamiento de efectivo — zona Este', acceptance: 'Predicción >4h antes; alerta P2 generada con cassette ID y niveles', priority: 'Critical', status: 'Pass' },
  { id: 'UAT-03', scenario: 'Pérdida de conectividad red — 10 ATMs simultáneos', acceptance: 'Todos detectados en <60s; mapa actualizado; alertas correlacionadas', priority: 'High', status: 'Pass' },
  { id: 'UAT-04', scenario: 'Cambio de imagen SO en 50 ATMs', acceptance: 'Mapa de cobertura SO actualizado en <5min; exportación CSV disponible', priority: 'High', status: 'Pending' },
  { id: 'UAT-05', scenario: 'Reporte diario disponibilidad JMBR', acceptance: 'PDF generado con datos por zona, modelo y período; formato aprobado', priority: 'Critical', status: 'Pending' },
  { id: 'UAT-06', scenario: 'Simulación stress test 933 ATMs x 100x', acceptance: 'Dashboard estable; latencia UI <2s; sin memory leaks en 8h continuas', priority: 'High', status: 'Pending' },
  { id: 'UAT-07', scenario: 'Integración GRG H22VL con catálogo canónico', acceptance: 'Todos los códigos GRG mapeados a canónicos; cero eventos sin clasificar', priority: 'Medium', status: 'Pass' },
  { id: 'UAT-08', scenario: 'Reconocimiento y cierre de alerta por operador', acceptance: 'Flujo ack→resolve completo en <5 clicks; historial persistido', priority: 'Medium', status: 'Pass' },
]

interface GapItem {
  feature: string
  atmvision: string
  ncrVision: string
  gap: 'None' | 'Partial' | 'Full'
}

const GAP_ANALYSIS: GapItem[] = [
  { feature: 'Real-time Fleet Dashboard', atmvision: 'React 18 + WebSocket, sub-segundo', ncrVision: 'APTRA Vision — polling 30s', gap: 'None' },
  { feature: 'Error Code Normalization', atmvision: 'Catálogo canónico multi-fabricante', ncrVision: 'Solo NCR native codes', gap: 'None' },
  { feature: 'Geographic Map View', atmvision: 'Leaflet con filtros avanzados', ncrVision: 'Disponible en módulo premium', gap: 'None' },
  { feature: 'Predictive Analytics', atmvision: 'Trend analysis + thresholds', ncrVision: 'ML avanzado (APTRA Analytics)', gap: 'Partial' },
  { feature: 'JMBR Regulatory Reports', atmvision: 'Motor diseñado — plantillas pendientes', ncrVision: 'Templates pre-aprobados SIB', gap: 'Partial' },
  { feature: 'Remote ATM Configuration', atmvision: 'No implementado', ncrVision: 'Disponible con APTRA Activate', gap: 'Full' },
  { feature: 'Biometric Customer Auth', atmvision: 'No en scope', ncrVision: 'Módulo opcional', gap: 'Full' },
  { feature: 'Hardware Fault Prediction (AI)', atmvision: 'Rule-based thresholds', ncrVision: 'AI/ML predictive maintenance', gap: 'Partial' },
]

const STATUS_COLOR: Record<CapStatus, string> = {
  Validated: '#22c55e', Pending: '#F89421', Gap: '#ef4444',
}

const UAT_STATUS_COLOR: Record<'Pass' | 'Pending' | 'Fail', string> = {
  Pass: '#22c55e', Pending: '#F89421', Fail: '#ef4444',
}

const GAP_COLOR: Record<GapItem['gap'], string> = {
  None: '#22c55e', Partial: '#F89421', Full: '#ef4444',
}

export default function NcrVisionBlueprint() {
  const C = useTheme()
  const [activeSection, setActiveSection] = useState<'capabilities' | 'uat' | 'gap' | 'rfp'>('capabilities')

  const validatedCount = MUST_HAVE.filter((c) => c.status === 'Validated').length
  const passCount = UAT_CRITERIA.filter((u) => u.status === 'Pass').length

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16, background: C.dark, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header Banner */}
      <div style={{ background: `linear-gradient(135deg, ${C.primary} 0%, #1b3a57 100%)`, border: `1px solid rgba(2,175,240,0.3)`, borderRadius: 12, padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 6 }}>
              Blueprint de Requerimientos
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
              NCR Vision — Especificación UAT
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
              Banreservas · Red de 933 ATMs · Q1 2026 · v1.0
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.green }}>{validatedCount}/{MUST_HAVE.length}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>Capacidades Validadas</div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.blue }}>{passCount}/{UAT_CRITERIA.length}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>Criterios UAT Aprobados</div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Nav */}
      <div style={{ display: 'flex', gap: 4 }}>
        {([
          ['capabilities', 'Must-Have Capabilities'],
          ['uat', 'Criterios UAT'],
          ['gap', 'Análisis de Brechas'],
          ['rfp', 'Resumen RFP'],
        ] as [typeof activeSection, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setActiveSection(id)} style={{
            background: activeSection === id ? C.primary : 'rgba(255,255,255,0.05)',
            border: `1px solid ${activeSection === id ? C.blue : C.border}`,
            color: activeSection === id ? '#fff' : C.muted,
            padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
            fontFamily: 'inherit', fontWeight: activeSection === id ? 600 : 400,
          }}>
            {label}
          </button>
        ))}
        <button
          onClick={() => alert('Generando Blueprint PDF...\n\nEsta función requiere el servicio report-engine en puerto 8003.')}
          style={{
            marginLeft: 'auto', background: C.orange, color: '#4a2a07',
            border: 'none', padding: '7px 16px', borderRadius: 6, cursor: 'pointer',
            fontSize: 12, fontFamily: 'inherit', fontWeight: 700,
          }}
        >
          Descargar Blueprint PDF
        </button>
      </div>

      {/* Must-Have Capabilities */}
      {activeSection === 'capabilities' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
            {MUST_HAVE.length} capacidades críticas identificadas para cumplimiento NCR Vision RFP
          </div>
          {MUST_HAVE.map((cap) => (
            <div key={cap.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', borderLeft: `3px solid ${STATUS_COLOR[cap.status]}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.muted }}>{cap.id}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{cap.name}</span>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  background: `${STATUS_COLOR[cap.status]}18`,
                  border: `1px solid ${STATUS_COLOR[cap.status]}50`,
                  color: STATUS_COLOR[cap.status],
                }}>
                  {cap.status}
                </span>
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{cap.description}</div>
              <div style={{ fontSize: 11, color: C.text, background: C.card2, padding: '6px 10px', borderRadius: 6 }}>
                {cap.notes}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* UAT Criteria */}
      {activeSection === 'uat' && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: C.card2 }}>
              <tr>
                {['ID', 'Escenario', 'Criterio de Aceptación', 'Prioridad', 'Estado'].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {UAT_CRITERIA.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.blue }}>{u.id}</td>
                  <td style={{ padding: '10px 14px', color: C.text, fontWeight: 500 }}>{u.scenario}</td>
                  <td style={{ padding: '10px 14px', color: C.muted, maxWidth: 320 }}>{u.acceptance}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                      background: u.priority === 'Critical' ? 'rgba(239,68,68,0.12)' : u.priority === 'High' ? 'rgba(249,115,22,0.12)' : 'rgba(248,148,33,0.12)',
                      color: u.priority === 'Critical' ? '#ef4444' : u.priority === 'High' ? '#f97316' : C.orange,
                    }}>
                      {u.priority}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                      background: `${UAT_STATUS_COLOR[u.status]}18`,
                      color: UAT_STATUS_COLOR[u.status],
                    }}>
                      {u.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Gap Analysis */}
      {activeSection === 'gap' && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
            Comparativa ATMVISION vs. NCR Vision (APTRA) — identificando brechas para el RFP
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: C.card2 }}>
              <tr>
                {['Capacidad', 'ATMVISION', 'NCR Vision', 'Brecha'].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GAP_ANALYSIS.map((g, i) => (
                <tr key={g.feature} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: C.text }}>{g.feature}</td>
                  <td style={{ padding: '10px 14px', color: C.muted, fontSize: 11 }}>{g.atmvision}</td>
                  <td style={{ padding: '10px 14px', color: C.muted, fontSize: 11 }}>{g.ncrVision}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                      background: `${GAP_COLOR[g.gap]}18`,
                      color: GAP_COLOR[g.gap],
                    }}>
                      {g.gap === 'None' ? 'Sin Brecha' : g.gap === 'Partial' ? 'Brecha Parcial' : 'Brecha Completa'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RFP Summary */}
      {activeSection === 'rfp' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            {
              title: 'Alcance del Sistema',
              color: C.blue,
              items: [
                '933 cajeros automáticos distribuidos en 4 zonas: Distrito Nacional, Norte, Sur, Este',
                '14 modelos activos NCR SelfServ (22, 22E, 23, 26, 27, 32, 38, 62, 82, 84, 88, 6682 FA/RA) + GRG H22VL',
                'Monitoreo 24/7 con disponibilidad del sistema >99.5%',
                'Integración con host bancario Banreservas y redes internacionales (VISA, MC)',
              ],
            },
            {
              title: 'Requerimientos Técnicos Mandatorios',
              color: C.orange,
              items: [
                'Latencia de eventos: p95 <500ms, p99 <2,000ms para alertas críticas',
                'Cobertura de protocolo: >95% de tipos de eventos de dispositivo',
                'Disponibilidad de plataforma: >99.9% SLA mensual',
                'Capacidad de almacenamiento: >180 días de histórico de eventos',
                'Soporte multi-fabricante: NCR, GRG, Diebold-Nixdorf sin agentes propietarios',
              ],
            },
            {
              title: 'Criterios de Evaluación RFP',
              color: C.green,
              items: [
                'Capacidad técnica demostrada con simulación de flota completa (ponderación 35%)',
                'Experiencia en banca dominicana y cumplimiento JMBR/SIB (ponderación 25%)',
                'TCO 5 años incluyendo licencias, mantenimiento y soporte (ponderación 25%)',
                'Plan de implementación y migración sin impacto operacional (ponderación 15%)',
              ],
            },
            {
              title: 'Entregables Esperados del Proveedor',
              color: '#a78bfa',
              items: [
                'Prueba de concepto (PoC) con 50 ATMs reales en 30 días',
                'Plan de integración con sistemas existentes (OPEX, XFS runtime)',
                'Documentación de API para integración con sistema de brigadas',
                'Capacitación para equipo NOC (20 operadores) y soporte técnico (5 ingenieros)',
                'SLA de soporte: P1 <2h respuesta, P2 <4h, P3 <NBD',
              ],
            },
          ].map((section) => (
            <div key={section.title} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: section.color, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 3, height: 16, background: section.color, borderRadius: 2, display: 'inline-block' }} />
                {section.title}
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {section.items.map((item, i) => (
                  <li key={i} style={{ color: C.muted, fontSize: 12, lineHeight: 1.5 }}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
